<div class="login-page" data-bs-theme="dark">
  <div class="login-container">
    <!-- Logo and Header -->
    <div class="text-center mb-4">
      <img [src]="logo" alt="ADN Logo" class="login-logo" />
      <h2 class="h3 mb-2">Enable Two-Factor Authentication</h2>
      <p class="text-muted mb-0">
        Add an extra layer of security to your admin account
      </p>
    </div>

    <!-- Progress Indicator -->
    <div class="progress-steps d-flex position-relative px-0 px-sm-3 mb-4">
      <div class="step flex-fill text-center position-relative" [class.active]="step === 'method-selection'" [class.completed]="step !== 'method-selection'">
        <div class="step-number rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold mb-2">1</div>
        <div class="step-label small">Choose Method</div>
      </div>
      <div class="step flex-fill text-center position-relative" [class.active]="step === 'totp-setup'" [class.completed]="step === 'backup-codes'">
        <div class="step-number rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold mb-2">2</div>
        <div class="step-label small">Setup</div>
      </div>
      <div class="step flex-fill text-center position-relative" [class.active]="step === 'backup-codes'">
        <div class="step-number rounded-circle d-inline-flex align-items-center justify-content-center fw-semibold mb-2">3</div>
        <div class="step-label small">Backup Codes</div>
      </div>
    </div>

    <!-- Error Alert -->
    <div *ngIf="errorMessage" class="alert alert-danger mb-4" role="alert">
      <i class="bi bi-exclamation-triangle-fill me-2"></i>
      {{ errorMessage }}
    </div>

    <!-- Step 1: Method Selection -->
    <div *ngIf="step === 'method-selection'">
      <form [formGroup]="setupForm" (ngSubmit)="onMethodSubmit()">
        <div class="mb-4">
          <label class="form-label">Select 2FA Method</label>

          <!-- TOTP Option -->
          <div class="method-option d-flex align-items-start p-3 mb-3 border border-2 rounded-3" [class.selected]="selectedMethod === '2fa-totp'">
            <input
              type="radio"
              class="form-check-input flex-shrink-0 mt-1 me-3"
              id="method-totp"
              formControlName="method"
              value="2fa-totp"
            />
            <label for="method-totp" class="d-flex align-items-start flex-grow-1 m-0">
              <div class="fs-2 lh-1 me-3 text-primary">
                <i class="bi bi-phone-fill"></i>
              </div>
              <div class="flex-grow-1">
                <h5 class="fs-6 mb-1">Authenticator App (Recommended)</h5>
                <p class="text-muted small mb-0">
                  Use Google Authenticator, Authy, or similar apps to generate verification codes
                </p>
              </div>
            </label>
          </div>

          <!-- SMS Option (Coming Soon) -->
          <div class="method-option disabled d-flex align-items-start p-3 mb-3 border border-2 rounded-3 opacity-50">
            <input
              type="radio"
              class="form-check-input flex-shrink-0 mt-1 me-3"
              id="method-sms"
              formControlName="method"
              value="2fa-sms"
              [disabled]="true"
            />
            <label for="method-sms" class="d-flex align-items-start flex-grow-1 m-0">
              <div class="fs-2 lh-1 me-3 text-primary">
                <i class="bi bi-chat-dots-fill"></i>
              </div>
              <div class="flex-grow-1">
                <h5 class="fs-6 mb-1">SMS Text Message <span class="badge bg-secondary ms-2 align-middle">Coming Soon</span></h5>
                <p class="text-muted small mb-0">
                  Receive verification codes via SMS text message
                </p>
              </div>
            </label>
          </div>

          <!-- Phone Number Input (for SMS) -->
          <div *ngIf="selectedMethod === '2fa-sms'" class="mt-3">
            <label for="phoneNumber" class="form-label">Phone Number</label>
            <input
              type="tel"
              class="form-control"
              id="phoneNumber"
              formControlName="phoneNumber"
              placeholder="+1 234 567 8900"
              [class.is-invalid]="setupForm.get('phoneNumber')?.invalid && setupForm.get('phoneNumber')?.touched"
            />
            <div
              *ngIf="setupForm.get('phoneNumber')?.invalid && setupForm.get('phoneNumber')?.touched"
              class="invalid-feedback"
            >
              Please enter a valid phone number with country code
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-secondary flex-fill" (click)="cancel()">
            Cancel
          </button>
          <button type="submit" class="btn btn-primary flex-fill" [disabled]="loading">
            <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
            Continue
          </button>
        </div>
      </form>
    </div>

    <!-- Step 2: TOTP Setup -->
    <div *ngIf="step === 'totp-setup'">
      <div class="mb-4">
        <h5 class="mb-3">Scan QR Code</h5>
        <p class="text-muted">
          Use your authenticator app to scan this QR code
        </p>

        <!-- QR Code Display -->
        <div class="d-flex justify-content-center p-4 mb-4 bg-white border rounded">
          <img [src]="qrCodeDataUrl" alt="QR Code" class="img-fluid" />
        </div>

        <!-- Manual Entry Option -->
        <div class="alert alert-info">
          <p class="mb-2 small">
            <strong>Can't scan the code?</strong>
          </p>
          <p class="mb-0 small">
            Enter the setup key manually in your authenticator app.
          </p>
        </div>
      </div>

      <form [formGroup]="setupForm" (ngSubmit)="onVerifyTOTP()">
        <!-- Verification Code Input -->
        <div class="mb-4">
          <label for="verificationCode" class="form-label">Enter Verification Code</label>
          <input
            type="text"
            class="form-control form-control-lg text-center fs-4 fw-semibold font-monospace verification-input"
            id="verificationCode"
            formControlName="verificationCode"
            placeholder="123456"
            maxlength="6"
            [class.is-invalid]="setupForm.get('verificationCode')?.invalid && setupForm.get('verificationCode')?.touched"
          />
          <div
            *ngIf="setupForm.get('verificationCode')?.invalid && setupForm.get('verificationCode')?.touched"
            class="invalid-feedback"
          >
            Please enter the 6-digit code from your authenticator app
          </div>
        </div>

        <!-- Actions -->
        <div class="d-flex gap-2">
          <button type="button" class="btn btn-outline-secondary flex-fill" (click)="cancel()">
            Cancel
          </button>
          <button type="submit" class="btn btn-primary flex-fill" [disabled]="loading || setupForm.get('verificationCode')?.invalid">
            <span *ngIf="loading" class="spinner-border spinner-border-sm me-2"></span>
            Verify & Continue
          </button>
        </div>
      </form>
    </div>

    <!-- Step 3: Backup Codes -->
    <div *ngIf="step === 'backup-codes'">
      <div class="alert alert-success mb-4">
        <i class="bi bi-check-circle-fill me-2"></i>
        Two-factor authentication has been enabled successfully!
      </div>

      <div class="mb-4">
        <h5 class="mb-3">Save Your Backup Codes</h5>
        <p class="text-muted">
          Store these backup codes in a safe place. Each code can only be used once to access your account if you lose access to your authenticator app.
        </p>

        <!-- Backup Codes Display -->
        <div class="bg-body-tertiary border rounded p-4">
          <div class="row row-cols-1 row-cols-sm-2 g-2">
            <div *ngFor="let code of backupCodes" class="col">
              <div class="font-monospace fw-semibold text-center py-2 bg-body border rounded">{{ code }}</div>
            </div>
          </div>
        </div>

        <!-- Download/Copy Actions -->
        <div class="d-flex gap-2 mt-3">
          <button type="button" class="btn btn-outline-primary flex-fill" (click)="downloadBackupCodes()">
            <i class="bi bi-download me-2"></i>
            Download
          </button>
          <button type="button" class="btn btn-outline-primary flex-fill" (click)="copyBackupCodes()">
            <i class="bi bi-clipboard me-2"></i>
            Copy
          </button>
        </div>
      </div>

      <!-- Warning -->
      <div class="alert alert-warning mb-4">
        <p class="mb-2">
          <i class="bi bi-exclamation-triangle-fill me-2"></i>
          <strong>Important:</strong>
        </p>
        <p class="mb-0 small">
          You won't be able to see these codes again. Make sure to save them before continuing.
        </p>
      </div>

      <!-- Finish Button -->
      <button type="button" class="btn btn-primary w-100" (click)="finishSetup()">
        <i class="bi bi-check-lg me-2"></i>
        I've Saved My Backup Codes
      </button>
    </div>
  </div>
</div>
